import { FC, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Add,
  ContentCopy,
  Delete,
  Download,
  DragIndicator,
  Edit,
  SpaceDashboardOutlined,
  TableChartOutlined,
} from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { firmIntelligenceUrl } from 'common/config/urls';
import { AI_DASHBOARD_SUMMARIES_ENABLER } from 'common/constants/features';
import {
  FIRM_SUMMARY_DEFINITION_DATA_QUERY_KEY,
  FIRM_VALUATIONS_DEFINITION_DATA_QUERY_KEY,
} from 'common/constants/services/firms';
import { useCurrencySelector, useFirmScopedPermissions } from 'common/hooks';
import { useStore } from 'common/store';
import type { DashboardDefinition } from 'common/types/dashboard/dashboardDefinition';
import {
  ColType,
  COLUMN_TYPES,
  Filter,
  FilterOp,
  getDefaultColumnsForSummaryType,
  getValueTypesForSummary,
  isClientOnlyValueType,
  isDashboardDefinition,
  normalizeFirmSpreadsheetDefinition,
  SortKey,
  SpreadsheetDefinition,
  SummaryDefinition,
  SummaryGroupBy,
} from 'common/types/summaryDefinitions';
import { DisplayFormatType } from 'common/types/summaryDefinitions/valueTypes/displayFormats';
import CurrencyMenu from 'components/CurrencyMenu';
import useCheckFeatures from 'components/FeatureEnabler/hooks';
import { PrimaryNavigationMenu, PrimaryNavigationMenuItem } from 'components/ui';
import { useTertiaryNavigationRegistration } from 'context/FirmBaseLayoutContext';
import type {
  TertiaryNavigationActionControlConfig,
  TertiaryNavigationLeftActionControlConfig,
} from 'context/FirmBaseLayoutContext';
import { OrderedDefinition, useSummaryDefinitions } from 'context/SummaryDefinitionsContext';
import { FIRM_SUMMARY_SPREADSHEET_DEFAULT_CURRENCY } from 'pages/Firms/common/constants';
import { isAddableFirmSummaryColumnType } from 'pages/Firms/hooks/useFirmSummarySheet/config/createColumns';
import EditColumnsPanel from 'pages/Firms/pages/FirmSummary/EditColumnsPanel';
import {
  FilterOpOption,
  getDisplayFormatTypeForColumn,
  getFilterOpsForColumnType,
  isOpValidForFormatType,
  validateFilterValue,
} from 'pages/Firms/pages/FirmSummary/filterValidation';
import { getObjectValue } from 'utilities';
import SummaryDashboardEditDialog from './components/SummaryDashboardEditDialog';
import FirmSummaryBody from './FirmSummaryBody';
import { useExportIntelligenceSummary } from './hooks/useExportIntelligenceSummary';
import { useIntelligenceContext } from './IntelligenceContext';
import {
  buildIntelligenceTertiaryNavigationItems,
  getCustomViewSlug,
  getDefaultFirmSummaryDefinition,
  parseCustomViewSlug,
} from './intelligenceNavigation';
import {
  INTELLIGENCE_SECONDARY_NAVIGATION_SLUGS,
  INTELLIGENCE_TERTIARY_NAVIGATION_LABELS,
  INTELLIGENCE_TERTIARY_NAVIGATION_SLUGS,
} from './intelligenceNavigation.constants';
import SummaryDashboard from './SummaryDashboard';
import { useDashboardDefinitionSave } from './SummaryDashboard/useDashboardDefinitionSave';
import useDashboardFieldAvailability, {
  isProcessManagementColumnEnabled,
} from './useDashboardFieldAvailability';
import { useSummaryAIAssist } from './useSummaryAIAssist';

const INTELLIGENCE_BASE_URL_FN = (slug: string) =>
  `${firmIntelligenceUrl(slug)}/${INTELLIGENCE_SECONDARY_NAVIGATION_SLUGS.SUMMARIES}`;

const NEW_DASHBOARD_BASE_NAME = 'New Dashboard';

const SUMMARY_ADD_MENU_TEST_IDS = {
  addButton: 'summary-add-view-button',
  dashboardOption: 'summary-add-view-dashboard',
  menu: 'summary-add-view-menu',
  spreadsheetOption: 'summary-add-view-spreadsheet',
};

interface FilterValueInputProps {
  value: string | number;
  formatType: DisplayFormatType;
  disabled?: boolean;
  onChange: (value: string) => void;
  onValidationChange: (valid: boolean) => void;
}

const FilterValueInput: FC<FilterValueInputProps> = ({ value, formatType, disabled, onChange, onValidationChange }) => {
  const [localValue, setLocalValue] = useState(String(value ?? ''));
  const validation = validateFilterValue(localValue, formatType);

  useEffect(() => {
    setLocalValue(String(value ?? ''));
  }, [value, formatType]);

  const handleTextChange = (newVal: string) => {
    setLocalValue(newVal);
    onValidationChange(validateFilterValue(newVal, formatType).valid);
  };

  if (formatType === 'boolean') {
    return (
      <FormControl size="small" sx={{ width: 150 }}>
        <InputLabel>Value</InputLabel>
        <Select
          label="Value"
          value={localValue}
          onChange={event => {
            setLocalValue(event.target.value);
            onChange(event.target.value);
            onValidationChange(true);
          }}
          disabled={disabled}>
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </Select>
      </FormControl>
    );
  }

  return (
    <TextField
      label="Value"
      size="small"
      type={formatType === 'date' ? 'date' : 'text'}
      value={localValue}
      onChange={event => handleTextChange(event.target.value)}
      onBlur={() => onChange(localValue)}
      disabled={disabled}
      error={!validation.valid}
      helperText={validation.error}
      InputLabelProps={formatType === 'date' ? { shrink: true } : undefined}
      sx={{ width: 150 }}
    />
  );
};

const normalizeSummaryName = (name: string) => name.trim().toLowerCase();

const generateCloneName = (sourceName: string, existingNames: string[]): string => {
  const baseName = sourceName.replace(/\s*\(Copy(?:\s+\d+)?\)\s*$/i, '').trim();
  const nameSet = new Set(existingNames.map(n => n.toLowerCase()));

  const firstCandidate = `${baseName} (Copy)`;
  if (!nameSet.has(firstCandidate.toLowerCase())) {
    return firstCandidate;
  }

  let counter = 2;
  while (nameSet.has(`${baseName} (Copy ${counter})`.toLowerCase())) {
    counter += 1;
  }
  return `${baseName} (Copy ${counter})`;
};

// Dashboards are created without a modal, so the generated name is the final one the user sees.
// Number it rather than reusing generateCloneName's "(Copy)" suffix, which would read as a copy.
const generateNumberedName = (baseName: string, existingNames: string[]): string => {
  const nameSet = new Set(existingNames.map(n => n.toLowerCase()));
  if (!nameSet.has(baseName.toLowerCase())) {
    return baseName;
  }

  let counter = 2;
  while (nameSet.has(`${baseName} ${counter}`.toLowerCase())) {
    counter += 1;
  }
  return `${baseName} ${counter}`;
};

const SortableListItem: FC<{
  id: string;
  children: React.ReactNode;
  secondaryAction?: React.ReactNode;
  disabled?: boolean;
}> = ({ id, children, secondaryAction, disabled }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: `${transition}, transform 200ms cubic-bezier(0.25, 0.1, 0.25, 1)`,
    opacity: isDragging ? 0 : 1,
    cursor: isDragging ? 'grabbing' : undefined,
  };

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      secondaryAction={secondaryAction}
      sx={{
        alignItems: 'flex-start',
        borderRadius: 1,
        transition: 'background-color 150ms ease',
        ...(isDragging && {
          backgroundColor: 'action.hover',
          boxShadow: 2,
        }),
      }}>
      <ListItemIcon
        sx={{
          minWidth: 32,
          alignSelf: 'center',
          cursor: disabled ? 'not-allowed' : 'grab',
          color: 'text.secondary',
          borderRadius: 1,
          transition: 'background-color 150ms ease',
          ...(!disabled && {
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }),
        }}
        {...(disabled ? {} : { ...attributes, ...listeners })}>
        <DragIndicator fontSize="small" />
      </ListItemIcon>
      {children}
    </ListItem>
  );
};

interface FirmSummaryWorkspaceProps {
  firmSlug: string;
  activeTertiaryTabSlug: string;
  baseUrlFn?: (firmSlug: string) => string;
  navigationSlugs?: { FIRM_SUMMARY: string };
  navigationLabels?: { FIRM_SUMMARY: string };
  navigationAriaLabel?: string;
  summaryType?: 'firm' | 'valuations';
}

const FirmSummaryWorkspace: FC<FirmSummaryWorkspaceProps> = ({
  firmSlug,
  activeTertiaryTabSlug,
  baseUrlFn = INTELLIGENCE_BASE_URL_FN,
  navigationSlugs = INTELLIGENCE_TERTIARY_NAVIGATION_SLUGS,
  navigationLabels = INTELLIGENCE_TERTIARY_NAVIGATION_LABELS,
  navigationAriaLabel = 'Intelligence views',
  summaryType = 'firm',
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const [storeValue] = useStore();
  const currentFirm = getObjectValue(getObjectValue(storeValue).firmInfo);
  // Process Management is a configurable column, but the firm flag gates its availability:
  // it is only offered in the picker / seeded default for valuations firms with the flag on.
  const isProcessManagementEnabled = isProcessManagementColumnEnabled({
    currentFirm,
    summaryType,
  });
  // The same gate expressed as dashboard column names, for the dashboard edit dialog's pickers.
  const dashboardFieldAvailability = useDashboardFieldAvailability(summaryType);

  // Gate Process Management out of the default set when the firm flag is off so seeded, reset,
  // and new views never persist a column the picker hides and rendering strips.
  const defaultColumns = useMemo(() => {
    const columns = getDefaultColumnsForSummaryType(summaryType);
    if (isProcessManagementEnabled) {
      return columns;
    }
    return columns.filter(column => column !== COLUMN_TYPES.PROCESS_MANAGEMENT);
  }, [summaryType, isProcessManagementEnabled]);

  const sortableSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const [activeDragId, setActiveDragId] = useState<string | null>(null);

  const {
    orderedDefinitions: allOrderedDefinitions,
    activeDefinition,
    setActiveDefinition,
    createDefinition,
    createDashboardDefinition,
    cloneDefinition,
    deleteDefinition,
    updateDefinition,
    refetch,
    isLoading,
    isFetching,
    isFetched,
    isSaving,
  } = useSummaryDefinitions();
  // Firm Admin, Fund Admin, Analyst and Full Access Viewer manage summaries; other viewer roles and
  // Auditor only view them. False until the role hydrates, so no mutating control flashes in.
  const { canExportIntelligenceSummary, canManageSummaries } = useFirmScopedPermissions();

  const [, checkFeature, areFeaturesReady] = useCheckFeatures();
  const isAiDashboardSummariesEnabled = checkFeature(AI_DASHBOARD_SUMMARIES_ENABLER);

  // With the flag off the feature is fully invisible, not just uncreatable: a dashboard made while
  // it was on must not keep a tab the user can no longer recreate or explain. Filtering here rather
  // than in SummaryDefinitionsContext keeps the gate in the view layer and lets every consumer
  // below inherit it — the tab strip, the kebab menu (so Clone, which dispatches on view_kind and
  // would otherwise be a back door to creating one, is unreachable), slugDefinition (so
  // SummaryDashboard never mounts), and the missing-slug effect below, which already redirects a
  // deep link whose id is absent from this list. Nothing is deleted; turning the flag back on
  // restores the tab and its dashboard.
  const orderedDefinitions = useMemo(
    () =>
      isAiDashboardSummariesEnabled
        ? allOrderedDefinitions
        : allOrderedDefinitions.filter(definition => !isDashboardDefinition(definition)),
    [allOrderedDefinitions, isAiDashboardSummariesEnabled]
  );

  const defaultDefinition = useMemo(() => getDefaultFirmSummaryDefinition(orderedDefinitions), [orderedDefinitions]);

  // Resolve which definition the current tertiary slug refers to.
  const slugDefinition = useMemo<OrderedDefinition | null>(() => {
    if (activeTertiaryTabSlug === navigationSlugs.FIRM_SUMMARY) {
      return defaultDefinition;
    }
    const definitionId = parseCustomViewSlug(activeTertiaryTabSlug);
    if (definitionId === null) {
      return null;
    }
    return orderedDefinitions.find(definition => definition.id === definitionId) ?? null;
  }, [activeTertiaryTabSlug, defaultDefinition, navigationSlugs.FIRM_SUMMARY, orderedDefinitions]);

  // Keep the context's active definition in sync with the URL.
  useEffect(() => {
    if (slugDefinition && slugDefinition.id !== activeDefinition?.id) {
      setActiveDefinition(slugDefinition.id);
    }
  }, [slugDefinition, activeDefinition?.id, setActiveDefinition]);

  // Gates the auto-seed below on the absence of a *spreadsheet* view rather than of any view at
  // all. Identical to a plain length check for every existing all-spreadsheet firm, but it means a
  // dashboard-only firm still gets the default Firm Summary tab that the default-slug fallback
  // resolves to. Derived as a boolean so the effect depends on a primitive and does not re-run on
  // every orderedDefinitions identity change.
  const hasSpreadsheetDefinition = useMemo(
    () => orderedDefinitions.some(definition => !isDashboardDefinition(definition)),
    [orderedDefinitions]
  );

  // When the firm has no summary definitions yet (no system global, no custom views),
  // seed a firm-owned "Firm Summary" definition with the default columns so the workspace
  // never lands on an empty body. Guarded by a ref so we only fire once per mount.
  //
  // The emptiness check must be based on a *settled* query, not `isLoading`. With React
  // Query v5, `isLoading` is only true on a cold fetch with no cached data; because the app
  // persists the query cache to IndexedDB (24h) with a 30s staleTime, most visits hydrate a
  // stale (sometimes empty) snapshot and revalidate in the background — during which
  // `isLoading` is false while `orderedDefinitions` is momentarily empty even though the firm
  // already has definitions. Seeding in that window created a duplicate default on nearly
  // every visit. Require the query to have finished fetching (`isFetched && !isFetching`) so
  // we only seed when the empty result is confirmed by the server.
  const autoSeedFiredRef = useRef(false);
  useEffect(() => {
    // View-only roles can't create definitions (the POST would 403); they keep the empty state.
    if (!canManageSummaries || !isFetched || isFetching || isSaving || autoSeedFiredRef.current) {
      return;
    }
    if (hasSpreadsheetDefinition) {
      return;
    }
    autoSeedFiredRef.current = true;
    // Seed with the flag-aware default column set so the persisted definition matches what the
    // picker exposes and the body renders (see defaultColumns above).
    createDefinition(navigationLabels.FIRM_SUMMARY, {
      columns: Object.fromEntries(defaultColumns.map((col, idx) => [idx, col])),
      sortKeys: [],
      groupBy: 'company',
      filters: [],
      filterExclusions: [],
    });
  }, [
    canManageSummaries,
    createDefinition,
    defaultColumns,
    hasSpreadsheetDefinition,
    isFetched,
    isFetching,
    isSaving,
    navigationLabels,
  ]);

  // If the URL points at a custom view that no longer exists (e.g. after delete),
  // redirect back to the default Firm Summary tab once the definitions list is loaded.
  //
  // Also wait for the feature flags to hydrate. `orderedDefinitions` hides dashboards while
  // AI_DASHBOARD_SUMMARIES_ENABLER reads off, and it reads off for the first moment of every load
  // because features come from IndexedDB — so without this guard a dashboard deep link is bounced to
  // Firm Summary even when the flag is on, and the flag hydrating afterwards cannot undo a
  // navigation that already happened.
  useEffect(() => {
    if (isLoading || !areFeaturesReady) {
      return;
    }
    if (activeTertiaryTabSlug === navigationSlugs.FIRM_SUMMARY) {
      return;
    }
    const definitionId = parseCustomViewSlug(activeTertiaryTabSlug);
    if (definitionId === null || !orderedDefinitions.some(definition => definition.id === definitionId)) {
      navigate(
        {
          pathname: `${baseUrlFn(firmSlug)}/${navigationSlugs.FIRM_SUMMARY}`,
          search: location.search,
        },
        { replace: true }
      );
    }
  }, [
    activeTertiaryTabSlug,
    baseUrlFn,
    firmSlug,
    areFeaturesReady,
    isLoading,
    location.search,
    navigate,
    navigationSlugs,
    orderedDefinitions,
  ]);

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuDefinitionId, setMenuDefinitionId] = useState<number | null>(null);
  const [addMenuAnchor, setAddMenuAnchor] = useState<HTMLElement | null>(null);
  const [dashboardEditDefinitionId, setDashboardEditDefinitionId] = useState<number | null>(null);
  // Which component tab the edit dialog should open on. Set by a tile's own edit button; the kebab
  // clears it so Edit Summary still lands on the Dashboard tab.
  const [dashboardEditComponentId, setDashboardEditComponentId] = useState<string | null>(null);
  const {
    errors: dashboardSaveErrors,
    isSaving: isDashboardSaving,
    resetErrors: resetDashboardSaveErrors,
    save: saveDashboardDefinition,
  } = useDashboardDefinitionSave({ summaryType });
  const [isCreatingDashboard, setIsCreatingDashboard] = useState(false);
  const [isColumnEditorOpen, setIsColumnEditorOpen] = useState(false);
  const [isCreatingNewView, setIsCreatingNewView] = useState(false);
  // Which definition the column editor was opened for. The kebab can target a tab other than the
  // active one, so the duplicate-name check cannot infer it from activeDefinition.
  const [editorDefinitionId, setEditorDefinitionId] = useState<number | null>(null);
  const [editorName, setEditorName] = useState('');
  const [editorColumns, setEditorColumns] = useState<ColType[]>([]);
  const [editorGroupBy, setEditorGroupBy] = useState<SummaryGroupBy>('company');
  const [editorSortKeys, setEditorSortKeys] = useState<SortKey[]>([]);
  const [editorFilters, setEditorFilters] = useState<Filter[]>([]);
  const [filterInputValidities, setFilterInputValidities] = useState<boolean[]>([]);
  const [editorBreakevenMultiple, setEditorBreakevenMultiple] = useState<string>('1');
  const [isEditorSaving, setIsEditorSaving] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState<{ columnType: ColType; dependents: ColType[] } | null>(null);

  const availableValueTypes = useMemo(() => getValueTypesForSummary('firm'), []);
  const valueTypeById = useMemo(
    () => new Map(availableValueTypes.map(valueType => [valueType.id, valueType])),
    [availableValueTypes]
  );
  // Sort and filter run server-side, so client-only columns (e.g. Process Management) have no
  // value to operate on. Restrict both dropdowns to the columns that can actually be sorted/filtered.
  const sortableFilterableColumns = useMemo(
    () =>
      editorColumns.filter(col => {
        const valueType = valueTypeById.get(col);
        return !valueType || !isClientOnlyValueType(valueType);
      }),
    [editorColumns, valueTypeById]
  );
  const requiredColumnIds = useMemo(
    () => availableValueTypes.filter(valueType => valueType.metadata?.isRequired).map(valueType => valueType.id),
    [availableValueTypes]
  );

  const requiredColumnsForEditor = useMemo(
    () =>
      // On the valuations page fund_name is a normal, removable/addable column, so it must not
      // be locked here (locked columns are dropped from the pickable list). Only company_name is
      // fixed as the row identity. The firm summary page keeps fund_name fixed as before.
      summaryType === 'valuations'
        ? new Set([...requiredColumnIds, 'company_name'])
        : new Set([...requiredColumnIds, 'company_name', 'fund_name']),
    [requiredColumnIds, summaryType]
  );

  const hasBreakevenColumn = useMemo(
    () => editorColumns.includes(COLUMN_TYPES.BREAKEVEN_EXIT_EQUITY_VALUE),
    [editorColumns]
  );
  const breakevenMultipleValue = Number(editorBreakevenMultiple);
  const isBreakevenMultipleInvalid =
    hasBreakevenColumn && (!Number.isFinite(breakevenMultipleValue) || breakevenMultipleValue <= 0);
  const filterOptionsPerFilter = useMemo<FilterOpOption[][]>(
    () =>
      editorFilters.map(([column]) => {
        const formatType = getDisplayFormatTypeForColumn(column, valueTypeById);
        return getFilterOpsForColumnType(formatType);
      }),
    [editorFilters, valueTypeById]
  );

  const filterValidations = useMemo(
    () =>
      editorFilters.map(([column, [, value]]) => {
        const formatType = getDisplayFormatTypeForColumn(column, valueTypeById);
        return validateFilterValue(value, formatType);
      }),
    [editorFilters, valueTypeById]
  );

  const hasInvalidFilters = useMemo(
    () => filterValidations.some(v => !v.valid) || filterInputValidities.some(v => !v),
    [filterValidations, filterInputValidities]
  );

  const handleFilterValidationChange = useCallback((index: number, valid: boolean) => {
    setFilterInputValidities(prev => prev.map((v, idx) => (idx === index ? valid : v)));
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
    setMenuDefinitionId(null);
  }, []);

  const getOrderedColumnTypes = useCallback(
    (definition?: SpreadsheetDefinition): ColType[] => {
      if (!definition?.columns || Object.keys(definition.columns).length === 0) {
        return defaultColumns;
      }

      return Object.entries(definition.columns)
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([, colType]) => colType);
    },
    [defaultColumns]
  );

  const canEditDefinition = useCallback(
    (definition?: SummaryDefinition | OrderedDefinition | null) => {
      if (!definition || !canManageSummaries) {
        return false;
      }
      if ('isGlobal' in definition && definition.isGlobal) {
        return Boolean(definition.firm || definition.fund);
      }
      return true;
    },
    [canManageSummaries]
  );

  const openColumnEditor = useCallback(
    (definitionToEdit: SummaryDefinition | OrderedDefinition | null = activeDefinition) => {
      if (!definitionToEdit || !canEditDefinition(definitionToEdit)) {
        return;
      }

      const currentDefinition = normalizeFirmSpreadsheetDefinition(
        definitionToEdit.spreadsheet_definition ?? definitionToEdit.spreadsheetDefinition
      );
      const orderedColumns = getOrderedColumnTypes(currentDefinition);
      const withRequired = [...orderedColumns];

      ['company_name', ...requiredColumnIds].forEach(requiredId => {
        if (!withRequired.includes(requiredId)) {
          withRequired.unshift(requiredId);
        }
      });

      setEditorColumns(withRequired);
      setEditorGroupBy(currentDefinition.groupBy ?? 'company');
      setEditorSortKeys(currentDefinition.sortKeys ?? []);
      const initialFilters = currentDefinition.filters ?? [];
      setEditorFilters(initialFilters);
      setFilterInputValidities(initialFilters.map(() => true));
      setEditorBreakevenMultiple(`${currentDefinition.breakevenMultiple ?? 1}`);
      setEditorName(definitionToEdit.name ?? '');
      setEditorDefinitionId(definitionToEdit.id ?? null);
      setIsCreatingNewView(false);
      setIsColumnEditorOpen(true);
    },
    [activeDefinition, canEditDefinition, getOrderedColumnTypes, requiredColumnIds]
  );

  const closeColumnEditor = useCallback(() => {
    setIsColumnEditorOpen(false);
    setIsCreatingNewView(false);
    setEditorDefinitionId(null);
    setEditorName('');
    setEditorColumns([]);
    setEditorGroupBy('company');
    setEditorSortKeys([]);
    setEditorFilters([]);
    setFilterInputValidities([]);
    setEditorBreakevenMultiple('1');
    setIsEditorSaving(false);
    setRemoveConfirm(null);
  }, []);

  // Exclude the definition being edited: keeping its own name in the list would make the duplicate
  // check reject the value the dialog opens with. Same rule as the dashboard editor's existingNames.
  const editorOtherNames = useMemo(
    () =>
      orderedDefinitions
        .filter(definition => definition.id !== editorDefinitionId)
        .map(definition => definition.name),
    [editorDefinitionId, orderedDefinitions]
  );

  // Rename used to own the duplicate-name rule; with that dialog gone this editor is the only path
  // to a spreadsheet summary's name, so it carries the check the dashboard editor already applies.
  const editorNameError = useMemo(() => {
    const trimmedName = editorName.trim();
    if (!trimmedName) {
      return 'Name is required.';
    }
    if (editorOtherNames.some(existing => normalizeSummaryName(existing) === normalizeSummaryName(trimmedName))) {
      return 'A summary with this name already exists.';
    }
    return null;
  }, [editorName, editorOtherNames]);

  const openCreateDialog = useCallback(() => {
    const existingNames = orderedDefinitions.map(d => d.name);
    let newName = 'New View';
    if (existingNames.some(n => n.toLowerCase() === newName.toLowerCase())) {
      newName = generateCloneName(newName, existingNames);
    }
    setEditorColumns([...defaultColumns]);
    setEditorGroupBy('company');
    setEditorSortKeys([]);
    setEditorFilters([]);
    setFilterInputValidities([]);
    setEditorBreakevenMultiple('1');
    setEditorName(newName);
    setEditorDefinitionId(null);
    setIsCreatingNewView(true);
    setIsColumnEditorOpen(true);
  }, [defaultColumns, orderedDefinitions]);

  // Only the AI dashboard flag turns "+" into a menu. With it off there is a single kind of summary
  // to create, so skip the one-option menu and open the column editor directly, exactly as the
  // button did before dashboards existed.
  const handleAddViewClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (!isAiDashboardSummariesEnabled) {
        openCreateDialog();
        return;
      }
      setAddMenuAnchor(event.currentTarget);
    },
    [isAiDashboardSummariesEnabled, openCreateDialog]
  );

  const handleAddMenuClose = useCallback(() => {
    setAddMenuAnchor(null);
  }, []);

  const handleCreateSpreadsheetSummary = useCallback(() => {
    handleAddMenuClose();
    openCreateDialog();
  }, [handleAddMenuClose, openCreateDialog]);

  // Dashboards need no configuration up front, so skip the editor dialog entirely: create the
  // definition and jump straight to its tab. Refetch before navigating (same reason as
  // handleSaveColumns) so the tab and body exist when the URL switches. No summary-data
  // invalidation here — a dashboard tab fetches no rows.
  const handleCreateDashboardSummary = useCallback(async () => {
    if (isCreatingDashboard || isSaving) {
      return;
    }
    handleAddMenuClose();
    setIsCreatingDashboard(true);
    try {
      const newDefinition = await createDashboardDefinition(
        generateNumberedName(
          NEW_DASHBOARD_BASE_NAME,
          orderedDefinitions.map(definition => definition.name)
        )
      );
      await refetch();
      navigate({
        pathname: `${baseUrlFn(firmSlug)}/${getCustomViewSlug(newDefinition.id)}`,
        search: location.search,
      });
    } finally {
      setIsCreatingDashboard(false);
    }
  }, [
    baseUrlFn,
    createDashboardDefinition,
    firmSlug,
    handleAddMenuClose,
    isCreatingDashboard,
    isSaving,
    location.search,
    navigate,
    orderedDefinitions,
    refetch,
  ]);

  const handleCloneDefinition = useCallback(
    async (definitionId: number) => {
      if (isSaving) {
        return;
      }
      const definitionToClone = orderedDefinitions.find(d => d.id === definitionId);
      if (!definitionToClone) {
        return;
      }
      handleMenuClose();
      const existingNames = orderedDefinitions.map(d => d.name);
      const clonedDefinition = await cloneDefinition(
        definitionId,
        generateCloneName(definitionToClone.name, existingNames)
      );
      if (clonedDefinition) {
        navigate({
          pathname: `${baseUrlFn(firmSlug)}/${getCustomViewSlug(clonedDefinition.id)}`,
          search: location.search,
        });
      }
    },
    [baseUrlFn, cloneDefinition, firmSlug, handleMenuClose, isSaving, location.search, navigate, orderedDefinitions]
  );

  const handleDeleteDefinition = useCallback(
    async (definitionId: number) => {
      handleMenuClose();
      await deleteDefinition(definitionId);
      // The redirect-on-stale-slug effect will return the user to firm-summary if the
      // deleted definition was active; nothing else to do here.
    },
    [deleteDefinition, handleMenuClose]
  );

  // Resolved by id rather than captured at click time so the dialog survives a refetch that lands
  // while it is open — a prompt-bar revision rewrites dashboard_definition underneath it. The
  // dialog seeds its draft once per opening, so it reads this for the first render only and a later
  // refetch cannot discard edits in progress.
  const dashboardEditDefinition = useMemo<OrderedDefinition | null>(() => {
    if (dashboardEditDefinitionId === null) {
      return null;
    }
    return orderedDefinitions.find(definition => definition.id === dashboardEditDefinitionId) ?? null;
  }, [dashboardEditDefinitionId, orderedDefinitions]);

  // Exclude the tab being edited: keeping its own name in the list would make the duplicate check
  // reject the value the dialog opens with.
  const dashboardEditOtherNames = useMemo(
    () => orderedDefinitions
      .filter(definition => definition.id !== dashboardEditDefinitionId)
      .map(definition => definition.name),
    [dashboardEditDefinitionId, orderedDefinitions]
  );

  const handleOpenDashboardEditDialog = useCallback(
    (definitionId: number, componentId?: string) => {
      handleMenuClose();
      resetDashboardSaveErrors();
      setDashboardEditComponentId(componentId ?? null);
      setDashboardEditDefinitionId(definitionId);
    },
    [handleMenuClose, resetDashboardSaveErrors]
  );

  // The per-tile edit button. Always targets the tab on screen, which is the definition the grid
  // was rendered from, so it needs no id of its own beyond the component's.
  const handleEditDashboardComponent = useCallback(
    (componentId?: string) => {
      if (!slugDefinition) {
        return;
      }
      handleOpenDashboardEditDialog(slugDefinition.id, componentId);
    },
    [handleOpenDashboardEditDialog, slugDefinition]
  );

  const handleCloseDashboardEditDialog = useCallback(() => {
    setDashboardEditDefinitionId(null);
    setDashboardEditComponentId(null);
  }, []);

  const handleSaveDashboardEdit = useCallback(
    async (name: string, definition: DashboardDefinition) => {
      if (dashboardEditDefinitionId === null) {
        return;
      }

      const saved = await saveDashboardDefinition({
        currentName: dashboardEditDefinition?.name ?? '',
        definition,
        definitionId: dashboardEditDefinitionId,
        name,
      });

      // Closing only on success is what keeps the draft from ever needing to reconcile with server
      // state: the next opening reseeds from the normalized payload the backend just stored.
      if (saved) {
        setDashboardEditDefinitionId(null);
        setDashboardEditComponentId(null);
      }
    },
    [dashboardEditDefinition, dashboardEditDefinitionId, saveDashboardDefinition]
  );

  const sortIds = useMemo(() => editorSortKeys.map((_, i) => `sort-${i}`), [editorSortKeys]);
  const filterIds = useMemo(() => editorFilters.map((_, i) => `filter-${i}`), [editorFilters]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(String(event.active.id));
  }, []);

  const handleReorderSortKeys = useCallback(
    (event: DragEndEvent) => {
      setActiveDragId(null);
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }
      const oldIndex = sortIds.indexOf(String(active.id));
      const newIndex = sortIds.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) {
        return;
      }
      setEditorSortKeys(prev => arrayMove(prev, oldIndex, newIndex));
    },
    [sortIds]
  );

  const handleReorderFilters = useCallback(
    (event: DragEndEvent) => {
      setActiveDragId(null);
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }
      const oldIndex = filterIds.indexOf(String(active.id));
      const newIndex = filterIds.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) {
        return;
      }
      setEditorFilters(prev => arrayMove(prev, oldIndex, newIndex));
      setFilterInputValidities(prev => arrayMove(prev, oldIndex, newIndex));
    },
    [filterIds]
  );

  const handleAddSortKey = useCallback(() => {
    const defaultColumn = sortableFilterableColumns[0];
    if (!defaultColumn) {
      return;
    }
    setEditorSortKeys(prev => [...prev, [defaultColumn, 'asc']]);
  }, [sortableFilterableColumns]);

  const handleUpdateSortColumn = useCallback((index: number, column: ColType) => {
    setEditorSortKeys(prev => prev.map((sortKey, idx) => (idx === index ? [column, sortKey[1]] : sortKey)));
  }, []);

  const handleUpdateSortDirection = useCallback((index: number, direction: SortKey[1]) => {
    setEditorSortKeys(prev => prev.map((sortKey, idx) => (idx === index ? [sortKey[0], direction] : sortKey)));
  }, []);

  const handleRemoveSortKey = useCallback((index: number) => {
    setEditorSortKeys(prev => prev.filter((_, idx) => idx !== index));
  }, []);

  const handleAddFilter = useCallback(() => {
    const defaultColumn = sortableFilterableColumns[0];
    if (!defaultColumn) {
      return;
    }
    setEditorFilters(prev => [...prev, [defaultColumn, ['=', '']] as Filter]);
    setFilterInputValidities(prev => [...prev, true]);
  }, [sortableFilterableColumns]);

  const handleUpdateFilterColumn = useCallback(
    (index: number, column: ColType) => {
      setEditorFilters(prev =>
        prev.map((filter, idx) => {
          if (idx !== index) {
            return filter;
          }
          const [, [currentOp, currentValue]] = filter;
          const newFormatType = getDisplayFormatTypeForColumn(column, valueTypeById);
          const validOps = getFilterOpsForColumnType(newFormatType);
          const nextOp = isOpValidForFormatType(currentOp, newFormatType) ? currentOp : validOps[0].value;
          const valueValid = validateFilterValue(currentValue, newFormatType).valid;
          const nextValue = valueValid ? currentValue : '';
          return [column, [nextOp, nextValue]] as Filter;
        })
      );
    },
    [valueTypeById]
  );

  const handleUpdateFilterOp = useCallback((index: number, op: FilterOp) => {
    setEditorFilters(prev => prev.map((filter, idx) => (idx === index ? [filter[0], [op, filter[1][1]]] : filter)));
  }, []);

  const handleUpdateFilterValue = useCallback((index: number, value: string | number) => {
    setEditorFilters(prev => prev.map((filter, idx) => (idx === index ? [filter[0], [filter[1][0], value]] : filter)));
  }, []);

  const handleRemoveFilter = useCallback((index: number) => {
    setEditorFilters(prev => prev.filter((_, idx) => idx !== index));
    setFilterInputValidities(prev => prev.filter((_, idx) => idx !== index));
  }, []);

  const handleConfirmRemove = useCallback(() => {
    if (!removeConfirm) {
      return;
    }

    const affected = [removeConfirm.columnType, ...removeConfirm.dependents];
    setEditorColumns(prev => prev.filter(col => !affected.includes(col)));
    setRemoveConfirm(null);
  }, [removeConfirm]);

  const handleCancelRemove = useCallback(() => {
    setRemoveConfirm(null);
  }, []);

  const handleSaveColumns = useCallback(async () => {
    if (isEditorSaving || isBreakevenMultipleInvalid || hasInvalidFilters) {
      return;
    }

    const trimmedName = editorName.trim();
    if (!trimmedName || editorNameError) {
      return;
    }

    const validColumns = new Set(editorColumns);
    const sanitizedSortKeys = editorSortKeys.filter(([col]) => validColumns.has(col));
    const sanitizedFilters = editorFilters.filter(
      ([col, [, value]]) => validColumns.has(col) && value !== null && typeof value !== 'undefined' && `${value}` !== ''
    );

    const nextDefinition: SpreadsheetDefinition = {
      columns: Object.fromEntries(editorColumns.map((col, idx) => [idx, col])),
      groupBy: editorGroupBy,
      sortKeys: sanitizedSortKeys,
      filters: sanitizedFilters,
      breakevenMultiple: hasBreakevenColumn ? breakevenMultipleValue : 1,
      filterExclusions: [],
    };

    try {
      setIsEditorSaving(true);

      let createdDefinitionId: number | null = null;

      if (isCreatingNewView) {
        const newDefinition = await createDefinition(trimmedName, nextDefinition);
        if (newDefinition) {
          createdDefinitionId = newDefinition.id;
        }
      } else if (activeDefinition) {
        const baseDefinition = normalizeFirmSpreadsheetDefinition(
          activeDefinition.spreadsheet_definition ?? activeDefinition.spreadsheetDefinition
        );
        await updateDefinition(activeDefinition.id, {
          name: trimmedName,
          spreadsheet_definition: { ...baseDefinition, ...nextDefinition },
        });
      }

      // Refetch before navigating so the new definition is already in orderedDefinitions
      // when the URL switches — otherwise the new tab and body show a perceptible lag.
      const definitionDataQueryKey =
        summaryType === 'valuations'
          ? FIRM_VALUATIONS_DEFINITION_DATA_QUERY_KEY
          : FIRM_SUMMARY_DEFINITION_DATA_QUERY_KEY;
      await Promise.all([refetch(), queryClient.invalidateQueries({ queryKey: [definitionDataQueryKey] })]);

      if (createdDefinitionId !== null) {
        navigate({
          pathname: `${baseUrlFn(firmSlug)}/${getCustomViewSlug(createdDefinitionId)}`,
          search: location.search,
        });
      }

      closeColumnEditor();
    } finally {
      setIsEditorSaving(false);
    }
  }, [
    activeDefinition,
    baseUrlFn,
    breakevenMultipleValue,
    closeColumnEditor,
    createDefinition,
    editorColumns,
    editorFilters,
    editorGroupBy,
    editorName,
    editorNameError,
    editorSortKeys,
    firmSlug,
    hasBreakevenColumn,
    hasInvalidFilters,
    isBreakevenMultipleInvalid,
    isCreatingNewView,
    isEditorSaving,
    location.search,
    navigate,
    queryClient,
    refetch,
    summaryType,
    updateDefinition,
  ]);

  // Kebab menu — anchored to the tertiary nav item's "more" button.
  const menuDefinition = useMemo<OrderedDefinition | null>(() => {
    if (menuDefinitionId === null) {
      return null;
    }
    return orderedDefinitions.find(definition => definition.id === menuDefinitionId) ?? null;
  }, [menuDefinitionId, orderedDefinitions]);

  const handleItemMenuClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>, item: { value: string }) => {
      event.stopPropagation();
      const definitionForSlug =
        item.value === navigationSlugs.FIRM_SUMMARY
          ? defaultDefinition
          : orderedDefinitions.find(definition => definition.id === parseCustomViewSlug(item.value));
      if (!definitionForSlug) {
        return;
      }
      setMenuDefinitionId(definitionForSlug.id);
      setMenuAnchor(event.currentTarget);
    },
    [defaultDefinition, navigationSlugs.FIRM_SUMMARY, orderedDefinitions]
  );

  // Register tertiary navigation items + add-view action + kebab handler with the layout shell.
  // Memoize each part so the shell only re-registers when meaningful state changes.
  const tertiaryNavItems = useMemo(
    () =>
      buildIntelligenceTertiaryNavigationItems(
        firmSlug,
        orderedDefinitions,
        location.search,
        baseUrlFn,
        navigationSlugs,
        navigationLabels,
        canManageSummaries
      ),
    [baseUrlFn, canManageSummaries, firmSlug, location.search, navigationLabels, navigationSlugs, orderedDefinitions]
  );

  // View-only roles get no "+": both spreadsheet and dashboard creation persist a definition.
  const tertiaryNavLeftActions = useMemo<TertiaryNavigationLeftActionControlConfig[]>(
    () =>
      canManageSummaries
        ? [
          {
            disabled: isCreatingDashboard,
            // Only advertise a popup when there really is one; with the flag off the button opens a dialog.
            hasPopup: isAiDashboardSummariesEnabled,
            key: 'add-view',
            onClick: handleAddViewClick,
            testId: SUMMARY_ADD_MENU_TEST_IDS.addButton,
            type: 'addButton',
          },
        ]
        : [],
    [canManageSummaries, handleAddViewClick, isAiDashboardSummariesEnabled, isCreatingDashboard]
  );

  // Currency/units selector on the right side of the tertiary nav (PRISM design).
  // The unit toggle re-scales the summary numbers; clicking the currency code opens
  // CurrencyMenu, and a selection re-fetches the exchange rate for the measurement
  // date registered in the layout page filters (via useFirmMeasurementDateFilter).
  const { format, formatDispatch } = useIntelligenceContext();
  const {
    isCurrencyLoading,
    currencyMenuAnchorEl,
    handleCurrencyMenuOpen,
    handleCurrencyMenuClose,
    handleCurrencyChange,
    handleUnitClick,
    unitDisplayLabel,
    currencyDisplayLabel,
    currencyTooltipTitle,
    currencies,
    usedCurrencies,
    showMore,
    showLess,
  } = useCurrencySelector({
    format,
    dispatch: formatDispatch,
    fallbackSourceCurrency: FIRM_SUMMARY_SPREADSHEET_DEFAULT_CURRENCY,
  });

  const {
    isDisabled: isAiAssistDisabled,
    isSubmitting: isAiAssistSubmitting,
    submitPrompt: submitAiAssistPrompt,
  } = useSummaryAIAssist({ definition: slugDefinition, summaryType });

  const { mutate: exportIntelligenceSummary, isPending: isExportingSummary } =
    useExportIntelligenceSummary();

  // Spreadsheet-kind tabs only: a dashboard carries its own prompt bar and has no
  // spreadsheet_definition for the assistant to revise.
  const isSpreadsheetView = Boolean(slugDefinition) && !isDashboardDefinition(slugDefinition);

  const handleExportSummary = useCallback(() => {
    if (!slugDefinition) {
      return;
    }
    exportIntelligenceSummary({
      definition: slugDefinition,
      summaryType,
      // Name the workbook after the tab: the default tab shows its fixed label, not the stored name.
      sheetName:
        activeTertiaryTabSlug === navigationSlugs.FIRM_SUMMARY ? navigationLabels.FIRM_SUMMARY : slugDefinition.name,
    });
  }, [
    activeTertiaryTabSlug,
    exportIntelligenceSummary,
    navigationLabels.FIRM_SUMMARY,
    navigationSlugs.FIRM_SUMMARY,
    slugDefinition,
    summaryType,
  ]);

  const tertiaryNavActions = useMemo<TertiaryNavigationActionControlConfig[]>(
    () => [
      // Never give this control `isVisible`: the host would wrap it in a transition whose
      // overflow:hidden clips the floating prompt panel. Gate by omitting the entry instead.
      ...(isSpreadsheetView && canManageSummaries
        ? [
          {
            type: 'aiPrompt',
            disabled: isAiAssistDisabled,
            isBusy: isAiAssistSubmitting,
            onSubmit: submitAiAssistPrompt,
          } as TertiaryNavigationActionControlConfig,
        ]
        : []),
      // Dashboards are not exported to Excel; only spreadsheet tabs get Export.
      ...(isSpreadsheetView && canExportIntelligenceSummary
        ? [
          {
            type: 'button',
            label: 'Export',
            variant: 'secondary',
            startIcon: <Download />,
            isSaving: isExportingSummary,
            disabled: isExportingSummary,
            testId: 'intelligence-summary-export-button',
            onClick: handleExportSummary,
          } as TertiaryNavigationActionControlConfig,
        ]
        : []),
      {
        type: 'currencySelector',
        currency: currencyDisplayLabel,
        currencyTooltipTitle,
        unit: unitDisplayLabel,
        onCurrencyClick: handleCurrencyMenuOpen,
        onUnitClick: handleUnitClick,
      },
    ],
    [
      canExportIntelligenceSummary,
      canManageSummaries,
      currencyDisplayLabel,
      currencyTooltipTitle,
      handleCurrencyMenuOpen,
      handleExportSummary,
      handleUnitClick,
      isAiAssistDisabled,
      isAiAssistSubmitting,
      isExportingSummary,
      isSpreadsheetView,
      submitAiAssistPrompt,
      unitDisplayLabel,
    ]
  );

  const tertiaryNavigationState = useMemo(
    () => ({
      ariaLabel: navigationAriaLabel,
      actions: tertiaryNavActions,
      items: tertiaryNavItems,
      leftActions: tertiaryNavLeftActions,
      onItemMenuClick: handleItemMenuClick,
    }),
    [handleItemMenuClick, navigationAriaLabel, tertiaryNavActions, tertiaryNavItems, tertiaryNavLeftActions]
  );

  useTertiaryNavigationRegistration(tertiaryNavigationState);

  const dialogTitleText = isCreatingNewView ? 'Create Summary View' : 'Edit Summary';
  const dialogSaveLabel = isCreatingNewView ? 'Create' : 'Save';

  // Build kebab menu items consistent with the original FirmSummary behavior:
  // - Show Clone to roles that can manage summaries (view-only roles get no kebab at all)
  // - When the definition is editable (canEditDefinition), prepend Edit Summary and append Delete
  const renderMenuItems = () => {
    if (!menuDefinition) {
      return null;
    }
    const isEditable = canEditDefinition(menuDefinition);
    // Edit Summary is one label over two editors: the column editor is spreadsheet-only, and a
    // dashboard gets the per-component dialog instead. Both carry the summary's name, so renaming
    // needs no menu item of its own.
    const isDashboard = isDashboardDefinition(menuDefinition);
    const items: React.ReactNode[] = [];

    if (isEditable && !isDashboard) {
      items.push(
        <PrimaryNavigationMenuItem
          key="edit-summary"
          icon={<Edit fontSize="inherit" />}
          onClick={() => {
            handleMenuClose();
            openColumnEditor(menuDefinition);
          }}
          disabled={isSaving || isEditorSaving}>
          Edit Summary
        </PrimaryNavigationMenuItem>
      );
    }

    // Same label and position as the spreadsheet branch above, so the menu reads identically for
    // both view kinds.
    if (isEditable && isDashboard) {
      items.push(
        <PrimaryNavigationMenuItem
          key="edit-dashboard-summary"
          icon={<Edit fontSize="inherit" />}
          onClick={() => handleOpenDashboardEditDialog(menuDefinition.id)}
          disabled={isSaving || isDashboardSaving}>
          Edit Summary
        </PrimaryNavigationMenuItem>
      );
    }

    if (canManageSummaries) {
      items.push(
        <PrimaryNavigationMenuItem
          key="clone"
          icon={<ContentCopy fontSize="inherit" />}
          onClick={() => handleCloneDefinition(menuDefinition.id)}
          disabled={isSaving}>
          Clone
        </PrimaryNavigationMenuItem>
      );
    }

    if (isEditable) {
      items.push(
        <Divider key="divider" />,
        <PrimaryNavigationMenuItem
          key="delete"
          icon={<Delete fontSize="inherit" />}
          onClick={() => handleDeleteDefinition(menuDefinition.id)}
          disabled={isSaving}
          tone="danger">
          Delete
        </PrimaryNavigationMenuItem>
      );
    }

    return items;
  };

  // Branch here rather than inside FirmSummaryBody: that component feeds spreadsheetDefinition
  // straight into useFirmSummaryTab, which fires the definition-scoped row queries. A dashboard
  // has no rows and no /data endpoint, so the spreadsheet body must never mount for one.
  const renderActiveTabBody = () => {
    if (!slugDefinition) {
      return null;
    }
    if (isDashboardDefinition(slugDefinition)) {
      return (
        <SummaryDashboard
          activeDefinition={slugDefinition}
          // Same gate as the kebab's Edit Summary item, so a dashboard the user cannot edit shows
          // no per-tile buttons either.
          onEditComponent={canEditDefinition(slugDefinition) ? handleEditDashboardComponent : undefined}
          summaryType={summaryType}
        />
      );
    }
    return (
      <FirmSummaryBody
        activeDefinition={slugDefinition}
        canEdit={canEditDefinition(slugDefinition)}
        summaryType={summaryType}
      />
    );
  };

  return (
    <>
      {renderActiveTabBody()}

      {isAiDashboardSummariesEnabled && (
        <PrimaryNavigationMenu
          anchorEl={addMenuAnchor}
          data-testid={SUMMARY_ADD_MENU_TEST_IDS.menu}
          open={Boolean(addMenuAnchor)}
          onClose={handleAddMenuClose}
          menuMinWidth={0}
          menuWidth="content"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
          <PrimaryNavigationMenuItem
            data-testid={SUMMARY_ADD_MENU_TEST_IDS.spreadsheetOption}
            icon={<TableChartOutlined fontSize="inherit" />}
            onClick={handleCreateSpreadsheetSummary}
            disabled={isSaving || isCreatingDashboard}>
            Spreadsheet summary
          </PrimaryNavigationMenuItem>
          <PrimaryNavigationMenuItem
            data-testid={SUMMARY_ADD_MENU_TEST_IDS.dashboardOption}
            icon={<SpaceDashboardOutlined fontSize="inherit" />}
            onClick={handleCreateDashboardSummary}
            disabled={isSaving || isCreatingDashboard}>
            Dashboard summary
          </PrimaryNavigationMenuItem>
        </PrimaryNavigationMenu>
      )}

      <PrimaryNavigationMenu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor && menuDefinition)}
        onClose={handleMenuClose}
        menuMinWidth={0}
        menuWidth="content"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        {renderMenuItems()}
      </PrimaryNavigationMenu>

      <SummaryDashboardEditDialog
        dashboardDefinition={dashboardEditDefinition?.dashboard_definition}
        definitionId={dashboardEditDefinitionId}
        existingNames={dashboardEditOtherNames}
        fieldAvailability={dashboardFieldAvailability}
        initialComponentId={dashboardEditComponentId}
        isSaving={isDashboardSaving}
        onClose={handleCloseDashboardEditDialog}
        onSave={handleSaveDashboardEdit}
        open={Boolean(dashboardEditDefinition)}
        saveErrors={dashboardSaveErrors}
        summaryName={dashboardEditDefinition?.name ?? ''}
      />

      <CurrencyMenu
        data-testid="firm-summary-currency-menu"
        anchorEl={currencyMenuAnchorEl}
        currencies={currencies}
        isCurrencyLoading={isCurrencyLoading}
        onClose={handleCurrencyMenuClose}
        onCurrencyChange={handleCurrencyChange}
        showMore={showMore}
        showLess={showLess}
        usedCurrencies={usedCurrencies}
      />

      <Dialog maxWidth="md" fullWidth open={isColumnEditorOpen} onClose={closeColumnEditor}>
        <DialogTitle>
          {dialogTitleText}
          {isCreatingNewView && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Name your view, choose which columns to display, and drag to reorder them.
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
            Name
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={editorName}
            onChange={event => setEditorName(event.target.value)}
            disabled={isEditorSaving}
            error={isColumnEditorOpen && Boolean(editorNameError)}
            helperText={(isColumnEditorOpen && editorNameError) || ' '}
          />

          <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
            Columns
          </Typography>
          <EditColumnsPanel
            availableColumns={availableValueTypes.filter(vt =>
              isAddableFirmSummaryColumnType(vt.id, isProcessManagementEnabled)
            )}
            allValueTypes={valueTypeById}
            columns={editorColumns}
            requiredColumns={requiredColumnsForEditor}
            defaultColumns={defaultColumns}
            onChange={setEditorColumns}
            disabled={isEditorSaving}
          />

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
            Sort order
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', marginBottom: 1 }}>
            Sorting uses the company&apos;s total values across all funds, not individual fund-level values.
          </Typography>
          <Button
            startIcon={<Add />}
            variant="outlined"
            onClick={handleAddSortKey}
            disabled={isEditorSaving || sortableFilterableColumns.length === 0}
            sx={{ marginBottom: 2 }}>
            Add Sort Key
          </Button>

          <List dense>
            <DndContext
              sensors={sortableSensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleReorderSortKeys}>
              <SortableContext items={sortIds} strategy={verticalListSortingStrategy}>
                {editorSortKeys.map(([column, direction], index) => {
                  const sortId = sortIds[index];

                  return (
                    <SortableListItem key={sortId} id={sortId} disabled={isEditorSaving}>
                      <Stack alignItems="center" direction="row" spacing={1} sx={{ width: '100%', minWidth: 0 }}>
                        <FormControl fullWidth size="small" sx={{ minWidth: 160 }}>
                          <InputLabel id={`sort-column-${index}`}>Column</InputLabel>
                          <Select
                            labelId={`sort-column-${index}`}
                            label="Column"
                            value={column}
                            onChange={event => handleUpdateSortColumn(index, event.target.value as ColType)}
                            disabled={isEditorSaving}>
                            {sortableFilterableColumns.map(col => (
                              <MenuItem key={`sort-col-${col}`} value={col}>
                                {valueTypeById.get(col)?.displayName ?? col}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <FormControl fullWidth size="small" sx={{ minWidth: 140 }}>
                          <InputLabel id={`sort-direction-${index}`}>Direction</InputLabel>
                          <Select
                            labelId={`sort-direction-${index}`}
                            label="Direction"
                            value={direction}
                            onChange={event => handleUpdateSortDirection(index, event.target.value as SortKey[1])}
                            disabled={isEditorSaving}>
                            <MenuItem value="asc">Ascending</MenuItem>
                            <MenuItem value="desc">Descending</MenuItem>
                          </Select>
                        </FormControl>

                        <IconButton
                          onClick={() => handleRemoveSortKey(index)}
                          disabled={isEditorSaving}
                          aria-label="Remove sort key"
                          sx={{ flexShrink: 0 }}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Stack>
                    </SortableListItem>
                  );
                })}
              </SortableContext>
              <DragOverlay dropAnimation={null}>
                {activeDragId?.startsWith('sort-')
                  ? (() => {
                    const idx = parseInt(activeDragId.replace('sort-', ''), 10);
                    const [column, direction] = editorSortKeys[idx] ?? [];
                    const displayName = valueTypeById.get(column)?.displayName ?? column;
                    return (
                      <Paper
                        elevation={8}
                        sx={{
                          px: 2,
                          py: 1.5,
                          cursor: 'grabbing',
                          borderRadius: 1,
                          minWidth: 220,
                          transform: 'rotate(2deg)',
                          boxShadow: 3,
                        }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <DragIndicator fontSize="small" color="action" />
                          <Typography variant="body2" fontWeight={500}>
                            {displayName} ({direction === 'asc' ? 'Asc' : 'Desc'})
                          </Typography>
                        </Stack>
                      </Paper>
                    );
                  })()
                  : null}
              </DragOverlay>
            </DndContext>
          </List>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
            Filters
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', marginBottom: 1 }}>
            Filtering uses the company&apos;s total values across all funds, not individual fund-level values.
          </Typography>
          <Button
            startIcon={<Add />}
            variant="outlined"
            onClick={handleAddFilter}
            disabled={isEditorSaving || sortableFilterableColumns.length === 0}
            sx={{ marginBottom: 2 }}>
            Add Filter
          </Button>

          <List dense>
            <DndContext
              sensors={sortableSensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleReorderFilters}>
              <SortableContext items={filterIds} strategy={verticalListSortingStrategy}>
                {editorFilters.map(([column, [op, value]], index) => {
                  const formatType = getDisplayFormatTypeForColumn(column, valueTypeById);
                  const filterOps = filterOptionsPerFilter[index] ?? [];
                  return (
                    <SortableListItem
                      // eslint-disable-next-line react/no-array-index-key -- filters may be duplicated (same column/op/value), so index is the only stable DnD identity
                      key={`filter-${index}`}
                      id={`filter-${index}`}
                      disabled={isEditorSaving}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={() => handleRemoveFilter(index)}
                          disabled={isEditorSaving}
                          aria-label="Remove filter">
                          <Delete fontSize="small" />
                        </IconButton>
                      }>
                      <Stack alignItems="center" direction="row" spacing={1} sx={{ width: '100%' }}>
                        <FormControl fullWidth size="small" sx={{ width: 160 }}>
                          <InputLabel id={`filter-column-${index}`}>Column</InputLabel>
                          <Select
                            labelId={`filter-column-${index}`}
                            label="Column"
                            value={column}
                            onChange={event => handleUpdateFilterColumn(index, event.target.value as ColType)}
                            disabled={isEditorSaving}>
                            {sortableFilterableColumns.map(col => (
                              <MenuItem key={`filter-col-${col}`} value={col}>
                                {valueTypeById.get(col)?.displayName ?? col}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <FormControl fullWidth size="small" sx={{ width: 130 }}>
                          <InputLabel id={`filter-op-${index}`}>Condition</InputLabel>
                          <Select
                            labelId={`filter-op-${index}`}
                            label="Condition"
                            value={op}
                            onChange={event => handleUpdateFilterOp(index, event.target.value as FilterOp)}
                            disabled={isEditorSaving}>
                            {filterOps.map(option => (
                              <MenuItem key={`filter-op-${option.value}`} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <FilterValueInput
                          value={value}
                          formatType={formatType}
                          disabled={isEditorSaving}
                          onChange={newValue => handleUpdateFilterValue(index, newValue)}
                          onValidationChange={valid => handleFilterValidationChange(index, valid)}
                        />
                      </Stack>
                    </SortableListItem>
                  );
                })}
              </SortableContext>
              <DragOverlay dropAnimation={null}>
                {activeDragId?.startsWith('filter-')
                  ? (() => {
                    const idx = parseInt(activeDragId.replace('filter-', ''), 10);
                    const [column, [op, value]] = editorFilters[idx] ?? [];
                    const displayName = valueTypeById.get(column)?.displayName ?? column;
                    const dragFormatType = getDisplayFormatTypeForColumn(column, valueTypeById);
                    const dragOps = getFilterOpsForColumnType(dragFormatType);
                    const opLabel = dragOps.find(o => o.value === op)?.label ?? op;
                    return (
                      <Paper
                        elevation={8}
                        sx={{
                          px: 2,
                          py: 1.5,
                          cursor: 'grabbing',
                          borderRadius: 1,
                          minWidth: 240,
                          transform: 'rotate(2deg)',
                          boxShadow: 3,
                        }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <DragIndicator fontSize="small" color="action" />
                          <Typography variant="body2" fontWeight={500}>
                            {displayName} {opLabel} {String(value || '')}
                          </Typography>
                        </Stack>
                      </Paper>
                    );
                  })()
                  : null}
              </DragOverlay>
            </DndContext>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeColumnEditor} disabled={isEditorSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveColumns}
            variant="contained"
            disabled={
              isEditorSaving ||
              editorColumns.length === 0 ||
              Boolean(editorNameError) ||
              isBreakevenMultipleInvalid ||
              hasInvalidFilters
            }>
            {dialogSaveLabel}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog maxWidth="xs" fullWidth open={Boolean(removeConfirm)} onClose={handleCancelRemove}>
        <DialogTitle>Remove dependent columns?</DialogTitle>
        <DialogContent dividers>
          {removeConfirm ? (
            <List dense>
              <ListItem>
                <ListItemText
                  primary={valueTypeById.get(removeConfirm.columnType)?.displayName ?? removeConfirm.columnType}
                  secondary="Selected column"
                />
              </ListItem>
              {removeConfirm.dependents.map(dependent => (
                <ListItem key={dependent}>
                  <ListItemText
                    primary={valueTypeById.get(dependent)?.displayName ?? dependent}
                    secondary="Dependent column"
                  />
                </ListItem>
              ))}
            </List>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRemove}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleConfirmRemove}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FirmSummaryWorkspace;
